<template>

 <div class="preview-container-wrapper" ref="containerWrapper">
    <div class="aspect-ratio-container" :style="aspectRatioStyle">
      <div
        class="preview-container"
        :style="{
          background: bgColor || '#f8f9fa',
        }"
      >
        <div v-if="items.length === 0" class="empty-state">
          <span>No items</span>
        </div>
        <div
          v-for="item in sortedItems"
          :key="item.id"
          class="preview-item"
          :style="getItemStyle(item)"
        >
          <Widget :config="item" :queueData="queueData" :queueConfig="queueConf" :queueAudioPlaying="voice_is_playing" :scale="scale"  />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, inject, watch, computed, reactive } from "vue";
import { displayService } from "@/_services/display.service";
import Widget from "@/components/dynamic/Widget.vue";
import { useI18n } from "vue-i18n";
import html2canvas from "html2canvas";

interface EchoInstance {
  connector: {
    pusher: {
      connection: {
        state: string;
      };
      connect: () => void;
      disconnect: () => void;
    };
  };
  channel: (name: string) => {
    listen: (event: string, callback: (data: any) => void) => void;
  };
}

// Inject dependencies
const echo = inject("echo") as EchoInstance;
const display = inject("display");
const snd = inject("snd") as HTMLAudioElement | null;
const audioNotification = inject("audioNotification2") as HTMLAudioElement | null;

const config = ref<any>({});
const items = ref<any[]>([]);
const bgColor = ref("#f8f9fa");
const screenDimension = ref("");
const isConnected = ref(false);
const onLine = ref(navigator.onLine);
const connectionInterval = ref<number | null>(null);

const containerWrapper = ref<HTMLElement | null>(null);
const containerWidth = ref(1280);
const containerHeight = ref(720);

const queueConf=reactive({
  counterTxt:"",
  numberTxt:"",
  numberHeaderColor:"",
  numberBgColor:"",
  numberTxtColor:"",
  counterHeaderColor:"",
  counterBgColor:"",
  counterTxtColor:"",
  queueData:[]
})

const queueData = ref<any[]>([]);
const blackScreen = ref(false);
const voices_to_play = ref<string[]>([]);
const voice_is_playing = ref(false);

let pingInterval: number | null = null;

function getDisplayQueues(queues: any): void {
  displayService.getInServiceTickets(queues).then((data: any) => {
    if (data && data.tickets) {
      queueData.value = data.tickets.map((ticket: any) => ({
        ticket: ticket.public_identifier,
        counter: ticket.counter_name,
        id: ticket.id
      }));
    } else {
      queueData.value = [];
    }
  });
}

function setupQueueListeners(displayId: any): void {
  if (!echo) return;
  const channel = echo.channel(`private-display-${displayId}`);
  if (!channel) return;
  (channel as any)
    .listen(".ticket-call", (data: any) => {
      updateQueueDataFromSocket(data);
    })
    .listen(".ticket-recall", (data: any) => {
      updateQueueDataFromSocket(data);
    })
    .listen(".black-screen", (data: any) => {
      if (data.status) {
        blackScreen.value = true
      } else {
        blackScreen.value = false
      }
    });
}

function buildVoiceText(data: any) {
  let text = "";
  if (
    localStorage.getItem("number") &&
    localStorage.getItem("counter")
  ) {
    const counterData = JSON.parse(localStorage.getItem("counter") || "{}");
    const numberData = JSON.parse(localStorage.getItem("number") || "{}");

    // if we have queue-specific voices
    if (counterData.queueVoices && data.queue_line_id) {
      const queueVoice = counterData.queueVoices.find((voice: any) => voice.queueId === data.queue_line_id);
      if (queueVoice) {
        text = numberData.voice +
               data.num_voice +
               queueVoice.voice +
               data.counter_voice;
      } else {
        text = numberData.voice +
               data.num_voice +
               counterData.voice +
               data.counter_voice;
      }
    } else {
      text = numberData.voice +
             data.num_voice +
             counterData.voice +
             data.counter_voice;
    }
  }
  return text;
}

function playVoices() {
  if (voices_to_play.value.length === 0 || voice_is_playing.value) return;
  voice_is_playing.value = true;
  if (audioNotification) {
    audioNotification.currentTime = 0;
    audioNotification.play();
    audioNotification.onended = () => {
      if (snd) {
        snd.src = "data:audio/mpeg;base64," + voices_to_play.value[0];
        try {
          snd.play();
          snd.onended = () => {
            voice_is_playing.value = false;
            voices_to_play.value.splice(0, 1);
            if (voices_to_play.value.length > 0) {
              playVoices();
            }
          };
        } catch (err) {
          voice_is_playing.value = false;
          voices_to_play.value.splice(0, 1);
        }
      } else {
        voice_is_playing.value = false;
        voices_to_play.value.splice(0, 1);
      }
    };
  } else {
    voice_is_playing.value = false;
    voices_to_play.value.splice(0, 1);
  }
}

function updateQueueDataFromSocket(data: any): void {
  if (data.in_service) {
    queueData.value = data.in_service.map((ticket: any) => ({
      ticket: ticket.public_identifier,
      counter: ticket.counter_name,
      id: ticket.id
    }));
  }
  // --- AUDIO LOGIC ---
  const voiceText = buildVoiceText(data);
  if (voiceText) {
    voices_to_play.value.push(voiceText);
    playVoices();
  }
}
const locale = ref("")
const initDisplay = (): void => {
  const displayData = JSON.parse(localStorage.getItem("display") || "{}");
  if (displayData) {
    config.value = displayData.config_screen || "{}";
    const parts = displayData.dimension.split("x");
    if (parts.length === 2) {
      const width = parseInt(parts[0]);
      const height = parseInt(parts[1]);
      if (!isNaN(width) && !isNaN(height)) {
        containerWidth.value = width;
        containerHeight.value = height;
      }
    }
    if (config.value) {
      parseConfigScreen(config.value, displayData);

      //language
      const conf = JSON.parse(displayData.config);
      locale.value= conf.locale
      localStorage.setItem('Language', locale.value);
      
    } else {
      console.warn("No config_screen found in display config");
    }

  
  }
};

const { t } = useI18n();

const parseConfigScreen = (configScreen: any, displayData: any): void => {
  if (configScreen?.bgColor) {
    bgColor.value = configScreen?.bgColor;
  }
  if (configScreen?.items && Array.isArray(configScreen?.items)) {
    items.value = configScreen?.items;
  } else {
    items.value = [];
  }
  const config = JSON.parse(displayData.config);
  if (config.queues) {
   
    const queueItem = items.value.find((item: any) => item.appType === "queue");
    console.log("queueItem",queueItem);
    if (queueItem) {
      const queueConfig = queueItem.queueConfig;
      queueConf.counterBgColor = queueConfig.counterBgColor;
      queueConf.counterHeaderColor = queueConfig.counterHeaderColor;
      queueConf.counterTxtColor = queueConfig.counterTxtColor;
      queueConf.numberBgColor = queueConfig.numberBgColor;
      queueConf.numberHeaderColor = queueConfig.numberHeaderColor;
      queueConf.numberTxtColor = queueConfig.numberTxtColor;
    }
    const voiceCall = config.voice_call;
    console.log("voiceCall",voiceCall);
    if (voiceCall == 1) {
      queueConf.counterTxt = config.textCounter;
      queueConf.numberTxt = config.textNumber;
    } else {
      const lang = locale.value;
      if(lang == "fr"){
        queueConf.counterTxt = "Guichet";
        queueConf.numberTxt = "Numéro";
      }else if(lang == "en"){
        queueConf.counterTxt = "Counter";
        queueConf.numberTxt = "Number";
      }else{
        queueConf.counterTxt = "شباك";
        queueConf.numberTxt = "رقم";
      }
      
      
    }
    // Initialize queue data and listeners
    getDisplayQueues(config.queues || displayData.queues || "");
    setupQueueListeners(displayData.id);
  }
};

const sortedItems = computed(() => {
  return [...items.value].sort((a, b) => {
    const zIndexA = a.zIndex || 1;
    const zIndexB = b.zIndex || 1;
    return zIndexB - zIndexA;
  });
});

const getItemStyle = (item: any) => {
  const x = getScaledPosition(item.x, "x");
  const y = getScaledPosition(item.y, "y");
  const width = getScaledSize(item.width || 200, "width");
  const height = getScaledSize(item.height || 150, "height");

  return {
    position: "absolute",
    left: `${x}px`,
    top: `${y}px`,
    width: `${width}px`,
    height: `${height}px`,
    zIndex: item.zIndex || 1,
    display: item.visible === false ? "none" : "block",
  };
};

const getScaledPosition = (value: number, axis: "x" | "y") => {
  if (!containerWrapper.value || value === undefined || value === null) {
    return value || (axis === "x" ? 100 : 100); // default position
  }

  const wrapperWidth = containerWrapper.value.clientWidth;
  const wrapperHeight =
    wrapperWidth * (containerHeight.value / containerWidth.value);

  if (axis === "x") {
    return (value / containerWidth.value) * wrapperWidth;
  } else {
    return (value / containerHeight.value) * wrapperHeight;
  }
};

const getScaledSize = (value: number, dimension: "width" | "height") => {
  if (!containerWrapper.value || value === undefined || value === null) {
    return dimension === "width" ? 200 : 150;
  }

  const wrapperWidth = containerWrapper.value.clientWidth;
  const wrapperHeight =
    wrapperWidth * (containerHeight.value / containerWidth.value);

  if (dimension === "width") {
    return (value / containerWidth.value) * wrapperWidth;
  } else {
    return (value / containerHeight.value) * wrapperHeight;
  }
};

const aspectRatioStyle = computed(() => {
  const ratio = (containerHeight.value / containerWidth.value) * 100;
  return {
    paddingBottom: `${ratio}%`,
  };
});


const scale = computed(() => {
  if (!containerWrapper.value) return 1;
  const wrapperWidth = containerWrapper.value.clientWidth;
  return wrapperWidth / containerWidth.value;
});

// cnx
const updateOnlineStatus = (e: Event) => {
  const { type } = e as { type: string };
  onLine.value = type === "online";
};

const checkConnection = () => {
  if (echo && echo.connector && echo.connector.pusher) {
    isConnected.value = echo.connector.pusher.connection.state === "connected";
    console.log(
      `WebSocket connection status: ${
        isConnected.value ? "connected" : "disconnected"
      }`
    );

    // reconnection 
    if (!isConnected.value) {
      echo.connector.pusher.connect(); // attempting to reconnect
    }
  }
};

const setupWebSocketListeners = () => {
  if (display && echo) {
    const displayData = JSON.parse(localStorage.getItem("display") || "{}");
    echo
      .channel(`private-display-${displayData.id}`)
      .listen(".display-refresh", () => {
        console.log("Received refresh signal, reloading...");
        displayService.refresh().catch(function (err) {
          console.log(err);
        });
      });
  }
};

watch(onLine, (isOnline) => {
  if (isOnline) {
    location.reload();
  }
});

function ping() {
  try {
    html2canvas(document.body, {
      scale: 1,
    }).then(function (canvas) {
      canvas.toBlob(
        function (blob) {
          if (!blob) return;
          let form = new FormData();
          var reader = new FileReader();
          reader.readAsDataURL(blob);
          reader.onloadend = function () {
            form.append("screenshot", reader.result as string);
            
            // systemInfo
            const systemInfo = localStorage.getItem('systemInfoList');
            if (systemInfo) {
              form.append("health", systemInfo);
              localStorage.removeItem('systemInfoList');
            }
            
            displayService
              .ping(form)
              .catch(function (err) {
                console.log(err);
              });
          };
        },
        "image/jpeg",
        0.05
      );
    });
  } catch (err) {
    console.log(err);
  }
}

onMounted(() => {
  localStorage.setItem("status", "dynamicPage")
  initDisplay();
  setupWebSocketListeners();
  checkConnection();
  connectionInterval.value = window.setInterval(checkConnection, 40000);
  window.addEventListener("online", updateOnlineStatus);
  window.addEventListener("offline", updateOnlineStatus);
  pingInterval = window.setInterval(ping, 300000); // 5 minutes
});

onBeforeUnmount(() => {
  window.removeEventListener("online", updateOnlineStatus);
  window.removeEventListener("offline", updateOnlineStatus);

  if (connectionInterval.value) {
    clearInterval(connectionInterval.value);
  }

  if (pingInterval) {
    clearInterval(pingInterval);
  }
  

  
  if (display && echo) {
    const displayData = JSON.parse(localStorage.getItem("display") || "{}");
    const channel = echo.channel(`private-display-${displayData.id}`);
    if (channel) {
      (channel as any).stopListening('.ticket-call');
      (channel as any).stopListening('.ticket-recall');
      (channel as any).stopListening('.black-screen');
      (channel as any).stopListening('.display-refresh');
    }
  }
});
</script>

<style scoped>
.preview-container-wrapper {
  width: 100%;
  position: relative;
  margin: 0;
  /* overflow: visible; */ /* old-screen */
  overflow: hidden;/* dynamic-screen */
  height: 100vh;/* dynamic-screen */
}

.aspect-ratio-container {
  width: 100%;
  position: relative;
  background: transparent;
}

.preview-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  outline: none;
  padding: 0;
  margin: 0;
}

.preview-item {
  overflow: visible;
  position: absolute;
  padding: 0;
  margin: 0;
}
/*
.preview-item :deep(img) {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.preview-item :deep(div),
.preview-item :deep(p),
.preview-item :deep(span) {
  display: block;
} 
 */

.preview-item :deep(.widget-ticker) {
  display: flex !important;
  visibility: visible !important;
  overflow: hidden !important;
  height: 100% !important;
}

.preview-item :deep(.ticker-track-horizontal) {
  display: flex !important;
  white-space: nowrap !important;
  align-items: center !important;
  height: auto !important;
  line-height: normal !important;
}

.preview-item :deep(.ticker-content) {
  display: inline-flex !important;
  flex-wrap: nowrap !important;
  align-items: center !important;
  white-space: nowrap !important;
  height: 100% !important;
}

.preview-item :deep(.ticker-text),
.preview-item :deep(.ticker-separator-container) {
  display: inline-flex !important;
  align-items: center !important;
  white-space: nowrap !important;
  height: 100% !important;
}

:fullscreen .preview-item :deep(.widget-ticker) {
  display: flex !important;
  visibility: visible !important;
  z-index: 10 !important;
  height: auto !important;
  width: 100% !important;
  overflow: hidden !important;
}

:fullscreen .preview-item :deep(.ticker-track-horizontal) {
  width: max-content !important;
  animation: ticker-horizontal 12s linear infinite !important;
  justify-content: flex-start !important;
  flex-wrap: nowrap !important;
  white-space: nowrap !important;
}

:fullscreen .preview-item :deep(.ticker-content) {
  display: inline-flex !important;
  visibility: visible !important;
  padding-right: 2rem !important;
  flex-wrap: nowrap !important;
  white-space: nowrap !important;
  width: auto !important;
}

.dimension-indicator {
  position: absolute;
  bottom: 5px;
  right: 10px;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  z-index: 1000;
}

.empty-state {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #777;
  font-size: 14px;
  text-align: center;
  width: 80%;
}

::deep(.ticker-track-horizontal) {
  width: max-content !important;
  animation: ticker-horizontal 12s linear infinite !important;
  will-change: transform;
}

@keyframes ticker-horizontal {
  0% {
    transform: translateX(0%);
  }
  100% {
    transform: translateX(-50%);
  }
}
</style>
